import { useCallback, useEffect, useMemo, useState } from 'react'
import { addServiceToPickup, removeServiceFromPickup, patchOrder, reopenOrder, consumePrepaidLbsForOrder, listPickupGroup, patchPickupGroup, activeCouponFor, redeemCouponForOrder } from '../lib/admin'
import { computeOrderTotal, fmtMoney, applyDiscount, discountLabel } from '../lib/pricing'
import { reconcileFreeShirt } from '../lib/promo'
import { SERVICE_LABEL } from '../lib/orders'
import { shortDate } from '../lib/dates'
import { showToast } from './Toast'
import MarkPaidModal from './MarkPaidModal'
import ServicePicker from './ServicePicker'
import { supabase } from '../lib/supabase'
import { sendInvoice } from '../lib/invoicing'
import {
  addOrderItem,
  deleteOrderItem,
  getMemberCycleUsedLbs,
  getService,
  getServiceByKey,
  listActiveServices,
  listItemsForService,
  listOrderItems,
} from '../lib/catalog'

function slotLbl(id) {
  if (id === 'morning') return '9–11 AM'
  if (id === 'evening') return '6–9 PM'
  return '—'
}

// Ticket — spec §4/§5.
//   editable = status === 'processing' && paid_at === null
// Anywhere else the ticket is read-only: no inputs, no buttons. The ticket
// itself has NO status action and NO payment button. Everything moves
// from the cards.
//
// Bottom bar (when editable):
//   [ Save ]  +  [ Send Invoice ]   → invoice not yet sent
//   [ Save ]  +  [ Mark as paid  ]   → invoice sent, awaiting payment
// "Send Invoice" is disabled while the ticket is dirty (unsaved changes),
// so Sébastien always sees the final total on screen before he pushes it.
//   - Save writes weighed_lbs / order_items / final_total_cents, closes
//     the ticket, no notification, order stays unpaid.
//   - Checkout does the same + paid_at = now(). Disabled if there is
//     neither a weight nor a single order_item.
export default function OrderPanel({ open, order, archived, onClose, onChanged }) {
  const editable = !archived && order?.status === 'processing' && !order?.is_paid

  const [lbs, setLbs] = useState('')
  const [service, setService] = useState(null)
  const [memberLbsUsed, setMemberLbsUsed] = useState(0)
  const [sendingInvoice, setSendingInvoice] = useState(false)
  const [markingPaid, setMarkingPaid] = useState(false)
  const [markPaidOpen, setMarkPaidOpen] = useState(false)
  const [orderItems, setOrderItems] = useState([])
  const [catalog, setCatalog] = useState([])
  // Result of the last consume_prepaid_lbs_for_order RPC call for THIS order.
  // Populated on ticket open (fresh read) and after every Save.
  const [consumption, setConsumption] = useState(null)
  const [addPickerOpen, setAddPickerOpen] = useState(false)
  // Which line's discount editor is open: item row id, 'service', or null.
  const [discountEditing, setDiscountEditing] = useState(null)
  // Active services — powers the Service dropdown. A booking can be
  // re-pointed at another service until the invoice goes out.
  const [allServices, setAllServices] = useState([])
  const [switchingService, setSwitchingService] = useState(false)

  // "Add service" — turns this lone order into a 2-service pickup. The RPC
  // creates the group, so the ticket reopens as the grouped one.
  const [addServiceOpen, setAddServiceOpen] = useState(false)
  const [addingService, setAddingService] = useState(false)

  // The customer's usable coupon, if they have one.
  const [coupon, setCoupon] = useState(null)
  const [applyingCoupon, setApplyingCoupon] = useState(false)
  useEffect(() => {
    if (!order?.user_id) { setCoupon(null); return }
    let alive = true
    activeCouponFor(order.user_id)
      .then((c) => { if (alive) setCoupon(c?.kind === 'amount_off' ? c : null) })
      .catch(() => { if (alive) setCoupon(null) })
    return () => { alive = false }
  }, [order?.user_id, order?.wf_discount_type])

  async function handleApplyCoupon() {
    if (!coupon || applyingCoupon) return
    setApplyingCoupon(true)
    try {
      await redeemCouponForOrder(order.id, coupon.code)
      setCoupon(null)
      showToast(`${coupon.code} applied`)
      // Re-read so the ticket shows the discount and the new total.
      onChanged?.({ ...order })
    } catch (err) {
      showToast(err?.message || 'Could not apply the coupon')
    } finally {
      setApplyingCoupon(false)
    }
  }

  // Cancelling is about the bag existing at all, not about weighing, so it
  // stays available at every stage until the money has moved.
  const [confirmCancel, setConfirmCancel] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const cancellable = !archived
    && !order?.is_paid
    && !order?.stripe_invoice_id
    && order?.status !== 'cancelled'
    && order?.status !== 'delivered'
  // Service id awaiting a second tap, when switching would orphan the
  // item lines already on the ticket.
  const [confirmSwitchTo, setConfirmSwitchTo] = useState(null)

  const refreshOrderItems = useCallback(async () => {
    if (!order?.id) { setOrderItems([]); return }
    try { setOrderItems(await listOrderItems(order.id)) }
    catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[panel] listOrderItems failed', err)
    }
  }, [order?.id])

  const refreshCatalog = useCallback(async () => {
    if (!order?.service_id) { setCatalog([]); return }
    try {
      // Own items first, then dry-cleaning garments cross-loaded so the
      // admin can drop dry-clean pieces on any ticket (a Wash & Fold
      // customer added a shirt at pickup, etc.). Deduped by id.
      const services = await listActiveServices()
      const dryId = services.find((s) => s.key === 'dry_cleaning')?.id || null
      const own = await listItemsForService(order.service_id)
      const dry = dryId && dryId !== order.service_id
        ? await listItemsForService(dryId)
        : []
      const seen = new Set()
      const merged = [...own, ...dry].filter((it) => {
        if (seen.has(it.id)) return false
        seen.add(it.id); return true
      })
      setCatalog(merged)
    }
    catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[panel] listItemsForService failed', err)
    }
  }, [order?.service_id])

  useEffect(() => { refreshOrderItems() }, [refreshOrderItems])
  useEffect(() => { refreshCatalog() }, [refreshCatalog])

  // service_id first, then a fallback on the `service` enum.
  //
  // GL-1117 was created straight in the database with `service = 'members'`
  // and no service_id. The panel priced nothing, so the ticket read as
  // permanently unsaved and Send Invoice stayed grey — a dead screen for a
  // row that was only missing a join key. The enum says which service it is;
  // looking the row up by key is the same answer the app would have stored.
  useEffect(() => {
    let alive = true
    ;(async () => {
      if (order?.service_id) {
        try {
          const row = await getService(order.service_id)
          if (alive && row) { setService(row); return }
        } catch { /* fall through to the key lookup */ }
      }
      if (order?.service) {
        try {
          const row = await getServiceByKey(order.service)
          if (alive) { setService(row || null); return }
        } catch { /* fall through */ }
      }
      if (alive) setService(null)
    })()
    return () => { alive = false }
  }, [order?.service_id, order?.service])

  // Service list for the dropdown, reloaded each time the ticket opens.
  useEffect(() => {
    if (!open) { setConfirmSwitchTo(null); return }
    let alive = true
    listActiveServices()
      .then((rows) => { if (alive) setAllServices(rows || []) })
      .catch(() => alive && setAllServices([]))
    return () => { alive = false }
  }, [open])

  useEffect(() => {
    if (!order?.user_id || service?.pricing_mode !== 'subscription') {
      setMemberLbsUsed(0)
      return
    }
    getMemberCycleUsedLbs(order.user_id, order.id)
      .then(setMemberLbsUsed)
      .catch(() => setMemberLbsUsed(0))
  }, [order?.user_id, order?.id, service?.pricing_mode])

  useEffect(() => {
    if (!order) return
    setLbs(order.weighed_lbs != null ? String(order.weighed_lbs) : '')
  }, [order])

  // Sibling orders on the same pickup (empty when this order stands alone).
  // Drives the group banner, the "waiting on…" invoice gate, and the
  // group-wide mark-as-paid.
  const [groupOrders, setGroupOrders] = useState([])
  useEffect(() => {
    if (!order?.pickup_group_id) { setGroupOrders([]); return }
    let alive = true
    listPickupGroup(order.pickup_group_id)
      .then((rows) => { if (alive) setGroupOrders(rows) })
      .catch(() => { if (alive) setGroupOrders([]) })
    return () => { alive = false }
  }, [order?.pickup_group_id, order?.final_total_cents, order?.weighed_lbs])

  // Current prepaid balance for the member owning this order. Fed into
  // preview so the shown total already reflects prepaid absorption of
  // overage (Sébastien sees the same amount that will be invoiced).
  const [memberPrepaid, setMemberPrepaid] = useState(0)

  // Load prior consumption row for this order so the breakdown shows
  // straight away (no Save needed) when re-opening a ticket. Members only.
  useEffect(() => {
    if (!order?.id || !order?.user_id) { setConsumption(null); setMemberPrepaid(0); return }
    let alive = true
    ;(async () => {
      const [{ data: cRow }, { data: bRow }] = await Promise.all([
        supabase
          .from('prepaid_lbs_consumption')
          .select('lbs_consumed, balance_after')
          .eq('order_id', order.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('prepaid_lbs_balance')
          .select('balance_lbs')
          .eq('profile_id', order.user_id)
          .maybeSingle(),
      ])
      if (!alive) return
      setMemberPrepaid(Number(bRow?.balance_lbs) || 0)
      if (cRow) {
        setConsumption({
          cycle_used: null,
          prepaid_used: Number(cRow.lbs_consumed) || 0,
          overage_lbs: null,
          balance_after: Number(cRow.balance_after) || 0,
        })
      } else {
        setConsumption(null)
      }
    })()
    return () => { alive = false }
  }, [order?.id, order?.user_id])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => e.key === 'Escape' && onClose?.()
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  const preview = useMemo(() => {
    if (!order || !service) return null
    const weight = Number(lbs) || 0
    // The prepaid balance we can offer this order = current balance + what
    // was previously debited for this same order (that debit gets reverted
    // and re-computed atomically by the RPC on Save).
    const prepaidAvailable = Number(memberPrepaid) + Number(consumption?.prepaid_used || 0)
    return computeOrderTotal({
      service,
      weighedLbs: weight,
      bags: order.bags,
      items: orderItems,
      memberLbsUsedThisMonth: memberLbsUsed,
      prepaidLbsAvailable: prepaidAvailable,
      serviceDiscount: order.wf_discount_type
        ? { type: order.wf_discount_type, value: order.wf_discount_value }
        : null,
    })
  }, [order, service, lbs, orderItems, memberLbsUsed, memberPrepaid, consumption])

  // Unsaved-edits flag. Computed here — above the `if (!open) return null`
  // below — because the effect that publishes it must run on every render,
  // open or closed. A hook placed after an early return makes React throw
  // "Rendered more hooks than during the previous render" (#310).
  const previewTotal = Number(preview?.totalCents || 0)
  const persistedTotal = Number(order?.final_total_cents || 0)

  // Why this ticket cannot be priced — '' when it can.
  //
  // ONE source, read by the Save button, by Save itself and by the Send
  // Invoice hint. Before this, a ticket that couldn't be priced reported
  // "Save changes first" (because previewTotal 0 ≠ the stored total) while
  // Save silently did nothing: the screen blamed the staff for not clicking
  // the button that was already broken.
  const pricingBlocker = !order
    ? 'No order loaded.'
    : !service
      ? `This ticket has no service on file${order.service ? ` — the row says “${order.service}”, but no matching service exists in the catalog` : ''}. Nothing can be priced or invoiced until that is fixed.`
      : !preview
        ? 'The price could not be computed for this ticket.'
        : ''

  // Once the invoice is out or the order is paid, the amount the customer
  // saw must never move under their feet. The free-shirt promo neither
  // applies nor clears past this line.
  const invoiceLocked = !!order?.paid_at || !!order?.stripe_invoice_id

  // The free shirt this ticket has earned but not yet saved. Shown in the
  // panel so Sébastien can check it BEFORE the invoice goes out — a
  // discount he only discovers afterwards is one he cannot verify.
  const pendingFreeShirt = useMemo(
    () => (preview?.freeShirt?.applied && !invoiceLocked ? preview.freeShirt : null),
    [preview, invoiceLocked],
  )

  // A ticket that can't be priced is never "dirty": there is nothing to
  // compare against, and saying so sent staff looking for edits that
  // didn't exist.
  const isDirty = editable
    && !pricingBlocker
    && (previewTotal !== persistedTotal || !!pendingFreeShirt)

  // Tell the staff auto-reloader to hold while this ticket has unsaved
  // edits (see lib/staffAutoReload.js).
  useEffect(() => {
    window.__ghostUnsaved = isDirty
    return () => { window.__ghostUnsaved = false }
  }, [isDirty])

  // ── Change the service the ticket is priced against (§7novies) ──────
  // Locked once the invoice is out, same rule as discounts: the amount the
  // customer already received must never move under their feet.
  const membersService = useMemo(
    () => allServices.find((sv) => sv.key === 'members') || null,
    [allServices],
  )
  const canChangeService =
    !archived && !order?.stripe_invoice_id && !order?.is_paid && !order?.delivered_at
  const serviceOptions = useMemo(
    () => allServices.filter((sv) =>
      sv.key !== 'members' || order?.customer_is_member || sv.id === order?.service_id,
    ),
    [allServices, order?.customer_is_member, order?.service_id],
  )

  async function handleServiceChange(nextId) {
    if (!order || !nextId || nextId === order.service_id) return
    const next = allServices.find((sv) => sv.id === nextId)
    if (!next) return
    // pricing.js prices 'subscription' at $0 base + free monthly lbs and
    // never checks membership itself, so the check has to happen here.
    if (next.key === 'members' && !order.customer_is_member) {
      showToast('This customer has no active membership')
      return
    }
    // Item lines were picked from the OLD service's catalogue, so they may
    // not belong to the new one. Require a second tap rather than silently
    // carrying them over.
    if (orderItems.length > 0 && confirmSwitchTo !== nextId) {
      setConfirmSwitchTo(nextId)
      showToast(`${orderItems.length} item line(s) may not belong to ${next.name} — tap again to confirm`)
      return
    }
    setSwitchingService(true)
    try {
      // handling_fee_pct is a snapshot on the row, so it has to follow the
      // new service instead of keeping the old service's copy.
      const updated = await patchOrder(order.id, {
        service: next.key,
        service_id: next.id,
        handling_fee_pct: next.handling_fee_pct,
      })
      setConfirmSwitchTo(null)
      onChanged?.({ ...order, ...updated })
      showToast(`Switched to ${next.name}`)
    } catch (err) {
      showToast(err?.message || 'Could not change the service')
    } finally {
      setSwitchingService(false)
    }
  }

  if (!open || !order) return null

  const address = [order.address_line1, order.address_line2, order.city, order.state, order.zip]
    .filter(Boolean).join(', ')
  const mapsHref = address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
    : null

  // Target for the "membership no longer active" banner below.
  const washFoldService = allServices.find((sv) => sv.key === 'wash_fold') || null

  // ── Save (§5) — persist weigh + order_items + final_total_cents.
  //    Silent: no toast, no email, just close the ticket. Stays unpaid.
  async function handleSaveOnly() {
    const saved = await persistWeighAndItems()
    // Only close on success. Closing after a failed save looked exactly
    // like a successful one.
    if (saved) onClose?.()
  }

  // ── Send Invoice — Stripe hosts + emails the invoice.
  //    Only enabled when the ticket is CLEAN (no unsaved edits) and
  //    final_total_cents > 0. The Netlify Function re-reads the order
  //    server-side; nothing about the amount is trusted from here.
  async function handleSendInvoice() {
    if (!order) return
    setSendingInvoice(true)
    try {
      const { stripe_invoice_id, hosted_invoice_url, reused, auto_paid } = await sendInvoice(order.id)
      // $0 invoices are auto-paid by Stripe → the function already wrote
      // paid_at server-side. Reflect it locally so the ticket closes right
      // away.
      const patch = auto_paid
        ? { stripe_invoice_id, paid_at: new Date().toISOString(), paid_method: 'stripe', is_paid: true }
        : { stripe_invoice_id }
      onChanged?.({ ...order, ...patch })
      if (auto_paid) {
        showToast('Members quota covered — receipt emailed ✅')
      } else {
        showToast(reused ? 'Invoice already sent — link reused' : 'Invoice sent to customer 📧')
        // SMS is now fired server-side inside send-invoice.js — guaranteed
        // to run and skipped on reused invoices via sms_logs idempotency.
      }
      onClose?.()
    } catch (err) {
      showToast(err?.message || 'Send invoice failed')
    } finally {
      setSendingInvoice(false)
    }
  }

  // ── Mark as paid — records payment in the DB. Called AFTER Sébastien
  //    has checked the Stripe dashboard and confirmed the invoice cleared.
  // Opens the "how did they pay?" modal. The write happens in
  // confirmMarkPaid once a method is picked.
  function handleMarkPaid() {
    if (!order) return
    setMarkPaidOpen(true)
  }

  // `method` is one of: stripe | cash | square | zelle | free.
  // It is stored as-is in orders.paid_method for the accounting view, and
  // shows up in the Telegram payment alert.
  async function confirmMarkPaid(method) {
    if (!order || !method) return
    setMarkingPaid(true)
    try {
      const patch = { paid_at: new Date().toISOString(), paid_method: method }
      // One pickup is billed once, so it gets paid once: stamp every order
      // in the group, not only the ticket that happens to be open.
      if (order.pickup_group_id) await patchPickupGroup(order.pickup_group_id, patch)
      const updated = await patchOrder(order.id, patch)
      onChanged?.({ ...order, ...updated })
      setMarkPaidOpen(false)
      showToast(method === 'free' ? 'Marked as free order' : `Marked as paid — ${method}`)
      onClose?.()
    } catch (err) {
      showToast(err?.message || 'Mark paid failed')
    } finally {
      setMarkingPaid(false)
    }
  }

  // Returns true when the ticket was saved, false otherwise — and says out
  // loud why when it can't. It used to `return` on the same condition
  // without a word, so Save looked like a button with nothing wired to it.
  async function persistWeighAndItems(extraPatch = null) {
    if (pricingBlocker) {
      showToast(`Can't save — ${pricingBlocker}`)
      return false
    }
    try {
      const weight = Number(lbs) || 0

      // ── Free-shirt promo ───────────────────────────────────────────
      // The discount is WRITTEN to order_items, not folded into a total.
      // send-invoice.js builds the invoice's discount lines by reading
      // order_items where discount_type is not null — so a promo that only
      // existed in the engine would charge the customer $24 instead of $32
      // and show them nothing explaining why. A smaller number with no
      // reason is worse than no discount at all.
      //
      // Locked tickets are left strictly alone: neither applying nor
      // clearing. Once the invoice is out, the amount the customer saw
      // never moves — same rule as every other discount here.
      let itemsForTotals = orderItems
      if (!invoiceLocked) {
        const writes = reconcileFreeShirt(preview.lines, preview.freeShirt)
        if (writes.length > 0) {
          await Promise.all(
            writes.map((w) => supabase.from('order_items').update(w.patch).eq('id', w.id)),
          )
          const byId = new Map(writes.map((w) => [w.id, w.patch]))
          itemsForTotals = orderItems.map((it) =>
            byId.has(it.id) ? { ...it, ...byId.get(it.id) } : it,
          )
        }
      }

      // Re-price with the discounts that were just written, so the stored
      // line totals and final_total_cents agree with them. computeOrderTotal
      // stays the only thing that works out money.
      const finalPreview = itemsForTotals === orderItems
        ? preview
        : computeOrderTotal({
            service,
            weighedLbs: weight,
            bags: order.bags,
            items: itemsForTotals,
            memberLbsUsedThisMonth: memberLbsUsed,
            prepaidLbsAvailable: Number(memberPrepaid) + Number(consumption?.prepaid_used || 0),
            serviceDiscount: order.wf_discount_type
              ? { type: order.wf_discount_type, value: order.wf_discount_value }
              : null,
          })

      // Refresh line_total_cents of any row whose stored snapshot drifted
      // from the freshly-priced bill (weight/bag changes, discounts).
      const updates = []
      for (const l of finalPreview.lines) {
        if (!l.id) continue
        if (Number(l.line_total_cents) === Number(l.lineTotalCents)) continue
        updates.push({ id: l.id, line_total_cents: l.lineTotalCents })
      }
      await Promise.all(
        updates.map((u) =>
          supabase.from('order_items').update({ line_total_cents: u.line_total_cents }).eq('id', u.id),
        ),
      )

      const patch = {
        weighed_lbs: weight || null,
        final_total_cents: finalPreview.totalCents,
        ...(extraPatch || {}),
      }
      const updated = await patchOrder(order.id, patch)

      // Debit prepaid balance for this order (spec §7ter). RPC is idempotent
      // — repeated saves revert the previous consumption then recompute.
      // Non-member / cancelled-member orders short-circuit to 0-debit.
      let rpcResult = null
      try {
        rpcResult = await consumePrepaidLbsForOrder(order.id)
        setConsumption(rpcResult)
        if (rpcResult?.balance_after != null) {
          setMemberPrepaid(Number(rpcResult.balance_after) || 0)
        }
        if (rpcResult?.prepaid_used > 0) {
          showToast(`Cycle ${rpcResult.cycle_used} lb · Prepaid ${rpcResult.prepaid_used} lb · Overage ${rpcResult.overage_lbs} lb`)
        }
      } catch (err) {
        // Don't fail the save if the RPC has an issue — the weight is
        // already persisted. Log and surface a soft warning.
        console.warn('[order-panel] consume prepaid failed', err)
        showToast(`Saved, but prepaid debit failed: ${err?.message || 'unknown'}`)
      }

      onChanged?.({ ...order, ...updated })
      return true
    } catch (err) {
      showToast(err?.message || 'Save failed')
      // Returning instead of rethrowing: the only caller uses the result to
      // decide whether to close, and an escaping rejection left the panel
      // open with no explanation.
      return false
    }
  }

  async function handleAddItem(item, quantity = 1) {
    try {
      const bagCount = order.bags === '4+' ? 4 : Math.max(1, parseInt(order.bags, 10) || 1)
      await addOrderItem(order.id, item, quantity, {
        bags: bagCount,
        weighedLbs: Number(lbs) || Number(order.weighed_lbs) || 0,
      })
      await refreshOrderItems()
      setAddPickerOpen(false)
      showToast(`${item.name} added`)
    } catch (err) {
      showToast(err?.message || 'Add failed')
    }
  }

  // Add a second service to this pickup. The RPC creates the group and
  // returns the NEW order; Admin then swaps this ticket for the grouped one,
  // which is where both bags get weighed.
  async function handleAddService(svc, bags) {
    if (addingService) return
    setAddingService(true)
    try {
      const created = await addServiceToPickup(order.id, svc.id, bags)
      setAddServiceOpen(false)
      showToast(`${svc.name} added — one pickup, two services`)
      // created.pickup_group_id is the group this order now belongs to.
      onChanged?.({ ...order, pickup_group_id: created?.pickup_group_id || null })
    } catch (err) {
      showToast(err?.message || 'Could not add the service')
    } finally {
      setAddingService(false)
    }
  }

  // A grouped order is cancelled through the RPC so the pickup can't be left
  // holding a single order — a group of one is not a thing.
  async function handleCancelOrder() {
    if (cancelling) return
    setCancelling(true)
    try {
      if (order.pickup_group_id) {
        await removeServiceFromPickup(order.id)
      } else {
        await patchOrder(order.id, { status: 'cancelled' })
      }
      setConfirmCancel(false)
      showToast(`${order.order_number} cancelled`)
      onChanged?.({ ...order, status: 'cancelled' })
      onClose?.()
    } catch (err) {
      showToast(err?.message || 'Could not cancel this order')
    } finally {
      setCancelling(false)
    }
  }

  // ── Discounts — written straight to the DB (the guard trigger blocks
  //    non-staff and anything after the invoice). The ticket then shows
  //    as dirty until Save persists the new total.
  async function applyItemDiscount(rowId, d) {
    try {
      const { error } = await supabase
        .from('order_items')
        .update({
          discount_type: d ? d.type : null,
          discount_value: d ? d.value : null,
          discount_reason: d ? d.reason || null : null,
        })
        .eq('id', rowId)
      if (error) throw error
      await refreshOrderItems()
      setDiscountEditing(null)
      showToast(d ? 'Discount applied — Save to update the total' : 'Discount removed')
    } catch (err) {
      showToast(err?.message || 'Discount failed')
    }
  }

  async function applyServiceDiscount(d) {
    try {
      const updated = await patchOrder(order.id, {
        wf_discount_type: d ? d.type : null,
        wf_discount_value: d ? d.value : null,
        wf_discount_reason: d ? d.reason || null : null,
      })
      onChanged?.({ ...order, ...updated })
      setDiscountEditing(null)
      showToast(d ? 'Discount applied — Save to update the total' : 'Discount removed')
    } catch (err) {
      showToast(err?.message || 'Discount failed')
    }
  }

  async function handleRemoveItem(rowId) {
    if (!window.confirm('Remove this item from the order?')) return
    try {
      await deleteOrderItem(rowId)
      await refreshOrderItems()
      showToast('Item removed')
    } catch (err) {
      showToast(err?.message || 'Remove failed')
    }
  }

  async function handleReopen() {
    if (!window.confirm('Reopen this order? It will move back to Processing.')) return
    try {
      const updated = await reopenOrder(order.id)
      onChanged?.({ ...order, ...updated })
      onClose?.()
      showToast('Order reopened')
    } catch (err) {
      showToast(err?.message || 'Reopen failed')
    }
  }

  // ── Send-Invoice gate ─────────────────────────────────────────────
  //   Enabled once the ticket is CLEAN (no unsaved edits) AND has real
  //   content (a weight OR items). $0 invoices are allowed — Stripe
  //   auto-marks them paid and still emails a receipt, which is the UX
  //   we want for members within their monthly quota.
  const hasContent = Number(order?.weighed_lbs) > 0 || orderItems.length > 0

  // One pickup ⇒ one invoice. It can only go out once EVERY order of the
  // group has been weighed or has items — otherwise we'd bill half a pickup.
  const groupPending = groupOrders.filter(
    (o) => o.id !== order?.id && !(Number(o.weighed_lbs) > 0 || Number(o.items_count) > 0),
  )
  const groupTotalCents = groupOrders.length > 1
    ? groupOrders.reduce((s, o) => s + Number(o.final_total_cents || 0), 0)
    : Number(order?.final_total_cents || 0)

  const canSendInvoice = editable && !pricingBlocker && !isDirty && hasContent && groupPending.length === 0
  // Order matters: the real cause first. A ticket that can't be priced used
  // to report "Save changes first", which sent staff clicking a Save that
  // could never succeed.
  const sendInvoiceHint = !editable
    ? ''
    : pricingBlocker
      ? pricingBlocker
      : !hasContent
        ? 'Save the ticket with a weight or items first.'
        : isDirty
          ? 'Save changes first — the shown total doesn’t match what will be invoiced.'
          : groupPending.length > 0
            ? `Waiting on ${groupPending.map((o) => o.order_number).join(', ')}`
            : ''

  const discountsLocked = !editable || !!order?.stripe_invoice_id

  const weighSummary = buildWeighSummary(service, Number(lbs) || 0, preview, memberLbsUsed)

  return (
    <div
      className="fixed inset-0 z-50 flex"
      role="dialog"
      aria-modal="true"
      aria-label={`Order ${order.order_number}`}
    >
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside
        className="ml-auto flex h-full w-full max-w-[400px] flex-col overflow-y-auto bg-[#F9FAFB] shadow-2xl"
        style={{ maxWidth: 'min(400px, 92vw)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-line bg-white px-4 py-3">
          <div className="font-bricolage text-[18px] font-extrabold text-navy">
            #{order.order_number}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-[20px] text-ink-3 hover:text-ink"
          >
            ✕
          </button>
        </header>

        <div className="space-y-3 p-4 pb-24">
          {/* Part of a multi-service pickup — one stop, one invoice. */}
          {groupOrders.length > 1 && (
            <section className="rounded-xl border border-aqua bg-aqua-soft p-3 text-[11px] leading-relaxed text-navy">
              🔗 Part of a {groupOrders.length}-order pickup ·{' '}
              <b>{groupOrders.map((o) => o.order_number).join(', ')}</b>
              {groupTotalCents > 0 && <> · group total <b>{fmtMoney(groupTotalCents)}</b></>}
              <div className="mt-1 text-navy/70">
                Everything returns in one trip. Send Invoice bills the whole pickup once.
              </div>
            </section>
          )}

          {/* Client */}
          <section className="rounded-xl border border-line bg-white p-4">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-ink-2">Client</div>
            <div className="font-bricolage text-[15px] font-extrabold text-navy">
              {order.customer_name || '—'}
            </div>
            {order.customer_phone && (
              <a href={`tel:${order.customer_phone}`} className="mt-1 block text-[12px] font-bold text-aqua">
                📞 {order.customer_phone}
              </a>
            )}
            {order.hotel_name && (
              <div className="mt-1 text-[12px] font-bold text-navy">
                🏨 {order.hotel_name}{order.room_number ? ` · Room ${order.room_number}` : ''}
              </div>
            )}
            {mapsHref && (
              <a
                href={mapsHref}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 block text-[12px] text-aqua hover:underline"
              >
                📍 {address}
              </a>
            )}
            {order.customer_email && (
              <div className="mt-1 text-[11px] text-ink-2">{order.customer_email}</div>
            )}
            {/* Which grid this order was priced against — frozen at booking,
                so it tells you what the customer was actually quoted. */}
            {order.pricing_zone_key && order.pricing_zone_key !== 'other' && (
              <div className="mt-1 text-[11px] font-bold text-navy">
                📍 {order.pricing_zone_key === 'ponte_vedra' ? 'Ponte Vedra' : 'South Florida'}
              </div>
            )}
          </section>

          {/* An active member who booked a pay-per-use service would have
              their monthly quota ignored and be billed on top of the $95
              they already pay. Caught here because the ticket is opened for
              every order at weigh-in. */}
          {order.customer_is_member && order.service !== 'members' && canChangeService && membersService && (
            <section className="rounded-xl border border-gold bg-gold/10 p-4">
              <div className="text-[12px] font-extrabold text-navy">
                🏆 This customer is an active Ghost Member
              </div>
              <div className="mt-1 text-[11px] text-ink-2">
                Booked as {service?.name || SERVICE_LABEL[order.service] || order.service} — their
                monthly quota will be ignored and they&apos;ll be charged on top of their subscription.
              </div>
              <button
                type="button"
                disabled={switchingService}
                onClick={() => handleServiceChange(membersService.id)}
                className="mt-2 rounded-lg bg-navy px-3 py-1.5 text-[11px] font-extrabold text-white disabled:opacity-50"
              >
                {switchingService
                  ? 'Switching…'
                  : confirmSwitchTo === membersService.id
                  ? 'Tap again to confirm'
                  : `Switch to ${membersService.name}`}
              </button>
            </section>
          )}

          {/* The reverse case: booked on the plan, but the membership has since
              ended or the card failed (the webhook flips customer_is_member
              off). pricing.js would still price it as a member — base $0 plus
              the monthly included lbs — because it never checks membership.
              Not auto-switched: an order booked while the customer was still a
              member may deserve to stay on the plan. Sébastien decides. */}
          {order.service === 'members' && !order.customer_is_member && canChangeService && washFoldService && (
            <section className="rounded-xl border border-red bg-red/10 p-4">
              <div className="text-[12px] font-extrabold text-navy">
                ⚠️ Membership no longer active
              </div>
              <div className="mt-1 text-[11px] text-ink-2">
                This order is on the Ghost Members plan, but the customer&apos;s membership has
                ended or their card failed. As it stands it will be priced as if they were still
                a member.
              </div>
              <button
                type="button"
                disabled={switchingService}
                onClick={() => handleServiceChange(washFoldService.id)}
                className="mt-2 rounded-lg bg-navy px-3 py-1.5 text-[11px] font-extrabold text-white disabled:opacity-50"
              >
                {switchingService
                  ? 'Switching…'
                  : confirmSwitchTo === washFoldService.id
                  ? 'Tap again to confirm'
                  : `Switch to ${washFoldService.name}`}
              </button>
            </section>
          )}

          {/* Order */}
          <section className="rounded-xl border border-line bg-white p-4">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-ink-2">Order</div>
            {canChangeService && serviceOptions.some((sv) => sv.id === order.service_id) ? (
              <div className="flex items-start justify-between gap-3 py-1 text-[12px]">
                <span className="text-ink-2">Service</span>
                <select
                  value={order.service_id || ''}
                  disabled={switchingService}
                  onChange={(e) => handleServiceChange(e.target.value)}
                  className="max-w-[62%] rounded-lg border border-line bg-white px-2 py-1 text-right text-[12px] font-bold text-navy disabled:opacity-50"
                >
                  {serviceOptions.map((sv) => (
                    <option key={sv.id} value={sv.id}>{sv.name}</option>
                  ))}
                </select>
              </div>
            ) : (
              <Row label="Service" value={service?.name || SERVICE_LABEL[order.service] || order.service} />
            )}
            <Row label="Bags" value={order.bags} />
            <Row
              label="Pickup"
              value={order.pickup_date ? `${shortDate(order.pickup_date)} · ${slotLbl(order.pickup_slot)}` : '—'}
            />
            <Row
              label="Delivery"
              value={
                order.return_tbd
                  ? 'TBD'
                  : order.return_date
                  ? `${shortDate(order.return_date)} · ${slotLbl(order.return_slot)}`
                  : '—'
              }
            />
          </section>

          {/* A winback coupon the customer was emailed. It is NEVER applied
              on its own — always a deliberate tap by staff. */}
          {coupon && !discountsLocked && !order?.wf_discount_type && (
            <section
              className="rounded-xl p-4"
              style={{ background: '#FEF9E7', border: '1px solid #F0DCA0' }}
            >
              <div className="font-bricolage text-[13px] font-extrabold" style={{ color: '#7A5E0C' }}>
                🎟️ ${((coupon.amount_cents || 0) / 100).toFixed(0)} coupon available — {coupon.code}
              </div>
              <div className="mt-1 text-[11px]" style={{ color: '#7A5E0C' }}>
                Applied to the subtotal, before the {Number(order?.handling_fee_pct || 12)}% handling fee.
                Expires {shortDate(String(coupon.expires_at).slice(0, 10))}.
              </div>
              <button
                type="button"
                onClick={handleApplyCoupon}
                disabled={applyingCoupon}
                className="mt-3 w-full rounded-lg bg-navy py-2.5 text-[12px] font-extrabold text-white disabled:opacity-50"
              >
                {applyingCoupon ? 'Applying…' : `Apply ${coupon.code}`}
              </button>
            </section>
          )}

          {/* Items & extras — service + overage always shown, locked */}
          <section className="rounded-xl border border-line bg-white p-4">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-[10px] font-bold uppercase tracking-wider text-ink-2">Items &amp; extras</div>
              {editable && catalog.length > 0 && (
                <button
                  type="button"
                  onClick={() => setAddPickerOpen((v) => !v)}
                  className="text-[11px] font-extrabold text-navy underline"
                >
                  {addPickerOpen ? 'Cancel' : '+ Add item'}
                </button>
              )}
            </div>

            <ul className="space-y-1.5">
              {/* Service line — always shown with 🔒 */}
              {service && (
                <ItemRow
                  name={`${service.name || 'Service'}${service.included_lbs ? ` — up to ${service.included_lbs} lbs` : ''}`}
                  unitInfo={service.pricing_mode === 'per_pound' ? 'flat forfait' : service.pricing_mode === 'subscription' ? 'monthly quota' : 'per-item base'}
                  cents={preview?.baseCents || 0}
                  locked
                />
              )}

              {/* Overage line — only when > 0 */}
              {(preview?.overageCents || 0) > 0 && service && (
                <ItemRow
                  name={`Overage · ${preview.overageLbs.toFixed(1)} lbs × $${((service.extra_lb_cents || 0) / 100).toFixed(2)}/lb`}
                  unitInfo="computed from weight"
                  cents={preview.overageCents}
                  locked
                />
              )}

              {/* Service discount — applies to base + overage together */}
              {service && service.pricing_mode !== 'per_item' && (preview?.serviceGrossCents || 0) > 0 && (
                <DiscountLine
                  label="Wash & Fold discount"
                  grossCents={preview.serviceGrossCents}
                  discountCents={preview.serviceDiscountCents}
                  type={order.wf_discount_type}
                  value={order.wf_discount_value}
                  reason={order.wf_discount_reason}
                  locked={discountsLocked}
                  open={discountEditing === 'service'}
                  onOpen={() => setDiscountEditing('service')}
                  onCancel={() => setDiscountEditing(null)}
                  onApply={applyServiceDiscount}
                />
              )}

              {/* Add-on lines (order_items) — deletable when editable */}
              {(preview?.lines || orderItems).map((li) => (
                <div key={li.id}>
                  <ItemRow
                    name={`${li.name_snapshot}${li.quantity > 1 ? ` × ${li.quantity}` : ''}`}
                    unitInfo={li.unit ? li.unit.replace('per_', 'per ') : ''}
                    cents={li.lineTotalCents ?? li.line_total_cents}
                    grossCents={li.lineDiscountCents > 0 ? li.lineGrossCents : null}
                    onRemove={editable ? () => handleRemoveItem(li.id) : undefined}
                  />
                  <DiscountLine
                    compact
                    grossCents={li.lineGrossCents ?? li.line_total_cents}
                    discountCents={li.lineDiscountCents || 0}
                    type={li.discount_type}
                    value={li.discount_value}
                    reason={li.discount_reason}
                    locked={discountsLocked}
                    open={discountEditing === li.id}
                    onOpen={() => setDiscountEditing(li.id)}
                    onCancel={() => setDiscountEditing(null)}
                    onApply={(d) => applyItemDiscount(li.id, d)}
                  />
                </div>
              ))}
            </ul>

            {addPickerOpen && editable && (
              <AddItemPicker catalog={catalog} onPick={handleAddItem} />
            )}
          </section>

          {/* The customer handed over something else too — add it to THIS
              pickup instead of opening a second ticket. The order becomes a
              2-service pickup and reopens as a grouped ticket. */}
          {editable && (
            <section className="rounded-xl border border-dashed border-line bg-white p-4">
              {!addServiceOpen ? (
                <button
                  type="button"
                  onClick={() => setAddServiceOpen(true)}
                  className="w-full rounded-xl border-[1.5px] border-navy bg-white px-3 py-2.5 font-bricolage text-[13px] font-extrabold text-navy"
                >
                  + Add service
                </button>
              ) : (
                <ServicePicker
                  services={allServices.filter((s) => s.id !== order.service_id)}
                  busy={addingService}
                  onCancel={() => setAddServiceOpen(false)}
                  onPick={handleAddService}
                />
              )}
            </section>
          )}

          {/* Client note */}
          {order.notes && (
            <section className="rounded-xl p-4" style={{ background: '#FEF9E7', color: '#7A5E0C' }}>
              <div className="mb-1 text-[10px] font-bold uppercase tracking-wider" style={{ color: '#7A5E0C' }}>
                Note from customer
              </div>
              <p className="text-[12px] leading-relaxed">{order.notes}</p>
            </section>
          )}

          {/* Weigh & total */}
          {service?.pricing_mode !== 'per_item' && (
            <section className="rounded-xl border border-line bg-white p-4">
              <div className="mb-3 text-[10px] font-bold uppercase tracking-wider text-ink-2">
                Weigh &amp; total
              </div>
              {editable ? (
                <>
                  <label className="field-label">Weighed lbs</label>
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.1"
                    min="0"
                    value={lbs}
                    onChange={(e) => setLbs(e.target.value)}
                    placeholder="e.g. 18.5"
                    className="field-input"
                  />
                  {service?.pricing_mode === 'subscription' && (
                    <p className="mt-1 text-[10px] text-ink-3">
                      Members quota: {service.monthly_included_lbs} lbs/month. Overage $
                      {((service.extra_lb_cents || 0) / 100).toFixed(2)}/lb.
                    </p>
                  )}
                </>
              ) : (
                <div className="text-[13px] font-bold text-navy">
                  {order.weighed_lbs != null ? `${Number(order.weighed_lbs).toFixed(1)} lbs` : '— lbs'}
                </div>
              )}

              {preview && weighSummary && (
                <div className="mt-3 text-[11px] font-bold text-navy">{weighSummary}</div>
              )}

              {service?.pricing_mode === 'subscription' && consumption && (
                <ConsumptionBreakdown c={consumption} />
              )}

              {preview && (
                <TotalBlock preview={preview} service={service} />
              )}
            </section>
          )}

          {service?.pricing_mode === 'per_item' && preview && (
            <section className="rounded-xl border border-line bg-white p-4">
              {/* The promo is only written to order_items on Save, so say so
                  here rather than let the total change after the click with
                  no warning. Once saved it appears as an ordinary discount
                  on the line itself. */}
              {pendingFreeShirt && (
                <div
                  className="mb-3 flex items-center justify-between rounded-lg px-3 py-2 text-[12px]"
                  style={{ background: '#E8F9FC', border: '1px solid #9FE4EF', color: '#0B2038' }}
                >
                  <span className="font-bold">
                    Free shirt — {pendingFreeShirt.shirtCount} shirts promo
                    <span className="ml-1 font-normal text-ink-2">
                      · already in the total, recorded on save
                    </span>
                  </span>
                  <span className="font-bricolage font-extrabold">
                    −{fmtMoney(pendingFreeShirt.amountCents)}
                  </span>
                </div>
              )}
              <TotalBlock preview={preview} service={service} compact />
            </section>
          )}

          {/* Cancel from the TICKET, at every stage before the money moves.
              The card only offers it while the order is still `booked`, so
              once it was marked picked up there was no way out at all. */}
          {cancellable && (
            <section className="rounded-xl border border-line bg-white p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-ink-2">
                Cancel this order
              </div>
              <p className="mt-1 text-[11px] leading-relaxed text-ink-3">
                Use this when the bag doesn’t exist — a double booking, or a customer
                who changed their mind. It leaves the board and stays in History.
              </p>
              {confirmCancel ? (
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirmCancel(false)}
                    className="flex-1 rounded-lg border border-line bg-white px-3 py-2 text-[12px] font-extrabold text-ink-2"
                  >
                    Keep it
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelOrder}
                    disabled={cancelling}
                    className="flex-1 rounded-lg bg-red px-3 py-2 text-[12px] font-extrabold text-white disabled:opacity-50"
                  >
                    {cancelling ? 'Cancelling…' : 'Tap again to cancel'}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmCancel(true)}
                  className="mt-2 w-full rounded-lg border-[1.5px] border-red bg-white px-3 py-2 text-[12px] font-extrabold text-red hover:bg-red/5"
                >
                  Cancel order
                </button>
              )}
            </section>
          )}

          {archived && (
            <button
              type="button"
              onClick={handleReopen}
              className="mt-2 w-full rounded-lg border border-line bg-white px-3 py-2 text-[12px] font-bold text-ink-2 hover:bg-[#F9FAFB]"
            >
              Reopen order
            </button>
          )}
        </div>

        {editable && (
          <footer className="sticky bottom-0 z-10 border-t border-line bg-white p-3">
            <div className="flex gap-2">
              {/* Stays clickable even when the ticket can't be priced: the
                  click is what produces the explanation. A disabled button
                  would be the silent failure in another costume. */}
              <button
                type="button"
                onClick={handleSaveOnly}
                title={pricingBlocker || undefined}
                className={`flex-1 rounded-xl border border-line bg-white px-3 py-3 font-bricolage text-[13px] font-extrabold text-navy ${
                  pricingBlocker ? 'opacity-60' : ''
                }`}
              >
                Save
              </button>
              {order.stripe_invoice_id ? (
                <button
                  type="button"
                  disabled
                  aria-live="polite"
                  className="flex-1 cursor-default rounded-xl px-3 py-3 font-bricolage text-[13px] font-extrabold"
                  style={{ background: '#FEF6DF', color: '#7A5E0C', border: '1px solid #F0DCA0' }}
                >
                  ⏳ Waiting for payment
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSendInvoice}
                  disabled={!canSendInvoice || sendingInvoice}
                  title={sendInvoiceHint}
                  className={`flex-1 rounded-xl bg-navy px-3 py-3 font-bricolage text-[13px] font-extrabold text-white ${(!canSendInvoice || sendingInvoice) ? 'opacity-50' : ''}`}
                >
                  {sendingInvoice ? 'Sending…' : 'Send Invoice'}
                </button>
              )}
            </div>
            {order.stripe_invoice_id && (
              <p className="mt-1 flex items-center justify-center gap-2 text-center text-[10px] text-ink-3">
                <span>Invoice sent · auto-updates when the customer pays.</span>
                <button
                  type="button"
                  onClick={handleMarkPaid}
                  disabled={markingPaid}
                  className="font-bold text-ink-2 underline hover:text-navy disabled:opacity-50"
                  title="Use only for cash / off-Stripe payments"
                >
                  {markingPaid ? 'Marking…' : 'Mark paid manually'}
                </button>
              </p>
            )}
            {/* No invoice: the customer can still pay in cash, by Square or
                Zelle — or the order is comped. Without this, marking such an
                order paid meant sending a Stripe invoice first. */}
            {!order.stripe_invoice_id && (
              <p className="mt-1 flex items-center justify-center gap-2 text-center text-[10px] text-ink-3">
                {!canSendInvoice && <span>{sendInvoiceHint}</span>}
                <button
                  type="button"
                  onClick={handleMarkPaid}
                  disabled={markingPaid}
                  className="font-bold text-ink-2 underline hover:text-navy disabled:opacity-50"
                  title="Cash, Square, Zelle or free order"
                >
                  {markingPaid ? 'Marking…' : 'Mark paid manually'}
                </button>
              </p>
            )}
          </footer>
        )}
      </aside>

      {/* "How did they pay?" — replaces the old confirm() so the method is
          recorded on the order (cash / Square / Zelle / free / Stripe). */}
      <MarkPaidModal
        open={markPaidOpen}
        order={order}
        busy={markingPaid}
        onClose={() => setMarkPaidOpen(false)}
        onConfirm={confirmMarkPaid}
      />
    </div>
  )
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1 text-[12px]">
      <span className="text-ink-2">{label}</span>
      <span className="text-right text-navy">{value}</span>
    </div>
  )
}

// Subtotal → handling → Total. Mirrors the client-side ReceiptCard so
// the admin sees the exact same breakdown the customer will get.
// Shows how this order's weight was split across the 50-lb cycle, prepaid
// balance, and overage — the RPC result. `null` fields (from a stale read
// before the first Save this session) render as em-dashes.
function ConsumptionBreakdown({ c }) {
  const fmt = (v) => v == null ? '—' : `${Number(v).toFixed(1)} lb`
  return (
    <div className="mt-3 rounded-lg border border-line bg-[#F9FAFB] p-3 text-[11px]">
      <div className="mb-1 text-[9px] font-extrabold uppercase tracking-widest text-ink-2">
        Prepaid breakdown
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <div className="text-ink-2">Cycle</div>
          <div className="font-bricolage font-extrabold text-navy">{fmt(c.cycle_used)}</div>
        </div>
        <div>
          <div className="text-ink-2">Prepaid</div>
          <div className="font-bricolage font-extrabold" style={{ color: '#B58A0F' }}>{fmt(c.prepaid_used)}</div>
        </div>
        <div>
          <div className="text-ink-2">Overage</div>
          <div className="font-bricolage font-extrabold" style={{ color: (c.overage_lbs || 0) > 0 ? '#EF4444' : '#0B2038' }}>
            {fmt(c.overage_lbs)}
          </div>
        </div>
      </div>
      {c.balance_after != null && (
        <div className="mt-2 border-t border-line pt-2 text-[10px] text-ink-2">
          Balance left: <b className="text-navy">{Number(c.balance_after).toFixed(1)} lbs prepaid</b>
        </div>
      )}
    </div>
  )
}

function TotalBlock({ preview, service, compact = false }) {
  const subtotal = Math.max(0, (preview?.totalCents || 0) - (preview?.handlingCents || 0))
  const handlingPct = Number(service?.handling_fee_pct || 0)
  return (
    <div className={`${compact ? '' : 'mt-3 border-t border-line pt-3'} space-y-1 text-[12px]`}>
      <div className="flex items-center justify-between">
        <span className="text-ink-2">Subtotal</span>
        <span className="font-bold text-navy">{fmtMoney(subtotal)}</span>
      </div>
      {preview?.handlingCents > 0 && (
        <div className="flex items-center justify-between">
          <span className="text-ink-2">
            Handling &amp; transportation {handlingPct ? `${handlingPct}%` : ''}
          </span>
          <span className="font-bold text-navy">{fmtMoney(preview.handlingCents)}</span>
        </div>
      )}
      <div className="mt-2 flex items-center justify-between border-t border-line pt-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-ink-2">Total</span>
        <span className="font-bricolage text-[20px] font-extrabold text-navy">
          {fmtMoney(preview.totalCents)}
        </span>
      </div>
    </div>
  )
}

// Discount control under a line: badge when applied, small button to open
// the inline editor, greyed out once the invoice is sent.
function DiscountLine({ label, compact, grossCents, discountCents, type, value, reason, locked, open, onOpen, onCancel, onApply }) {
  const has = !!type
  if (open && !locked) {
    return (
      <DiscountEditor
        initial={has ? { type, value, reason } : null}
        grossCents={grossCents}
        onCancel={onCancel}
        onApply={onApply}
      />
    )
  }
  return (
    <div className={`flex items-center justify-between gap-2 ${compact ? 'pb-1 pl-0.5' : 'py-1'}`}>
      <div className="min-w-0 flex-1 text-[10px]">
        {!compact && <span className="mr-1 font-bold text-ink-2">{label}</span>}
        {has && (
          <span className="rounded-full bg-red/10 px-2 py-0.5 font-extrabold text-red">
            Discount {discountLabel(type, value)}{reason ? ` · ${reason}` : ''}
          </span>
        )}
        {has && !compact && discountCents > 0 && (
          <span className="ml-1 font-bold text-red">−{fmtMoney(discountCents)}</span>
        )}
      </div>
      <button
        type="button"
        onClick={onOpen}
        disabled={locked}
        title={locked ? 'Invoice already sent' : has ? 'Edit discount' : 'Add a discount'}
        className="shrink-0 rounded-md border border-line px-2 py-0.5 text-[10px] font-extrabold text-ink-2 hover:bg-[#F3F4F6] disabled:cursor-not-allowed disabled:opacity-40"
      >
        {has ? 'Edit %' : '% Discount'}
      </button>
    </div>
  )
}

function DiscountEditor({ initial, grossCents, onCancel, onApply }) {
  const [type, setType] = useState(initial?.type || 'amount')
  const [value, setValue] = useState(initial?.value != null ? String(initial.value) : '')
  const [reason, setReason] = useState(initial?.reason || '')
  const [busy, setBusy] = useState(false)
  const v = Number(value)
  const error = value === ''
    ? ''
    : !(v > 0)
      ? 'Enter a value above 0'
      : type === 'percent' && v > 100
        ? 'Max 100%'
        : ''
  const after = error || value === '' ? null : applyDiscount(grossCents, type, v)

  async function submit(d) {
    setBusy(true)
    await onApply(d)
    setBusy(false)
  }

  return (
    <div className="my-1 rounded-lg border border-line bg-[#F9FAFB] p-2.5">
      <div className="flex items-center gap-2">
        <div className="flex overflow-hidden rounded-md border border-line bg-white text-[12px] font-extrabold">
          {[['amount', '$'], ['percent', '%']].map(([k, lbl]) => (
            <button
              key={k}
              type="button"
              onClick={() => setType(k)}
              className={`px-3 py-1.5 ${type === k ? 'bg-navy text-white' : 'text-navy'}`}
            >
              {lbl}
            </button>
          ))}
        </div>
        <input
          type="number"
          inputMode="decimal"
          min="0"
          max={type === 'percent' ? 100 : undefined}
          step="0.01"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={type === 'percent' ? '10' : '2.00'}
          className="field-input !py-1.5"
          autoFocus
        />
      </div>
      <input
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason (optional) — e.g. Loyal customer"
        maxLength={120}
        className="field-input mt-2 !py-1.5 !text-[12px]"
      />
      <div className="mt-1.5 min-h-[14px] text-[10px]">
        {error
          ? <span className="font-bold text-red">{error}</span>
          : after != null && (
            <span className="text-ink-2">
              <s>{fmtMoney(grossCents)}</s> → <b className="text-navy">{fmtMoney(after)}</b>
            </span>
          )}
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        <button
          type="button"
          disabled={busy || !!error || value === ''}
          onClick={() => submit({ type, value: v, reason: reason.trim() })}
          className="rounded-md bg-navy px-3 py-1.5 text-[11px] font-extrabold text-white disabled:opacity-50"
        >
          Apply
        </button>
        <button type="button" onClick={onCancel} className="px-2 py-1.5 text-[11px] font-bold text-ink-2">
          Cancel
        </button>
        {initial && (
          <button
            type="button"
            disabled={busy}
            onClick={() => submit(null)}
            className="ml-auto px-2 py-1.5 text-[11px] font-extrabold text-red"
          >
            Remove discount
          </button>
        )}
      </div>
    </div>
  )
}

function ItemRow({ name, unitInfo, cents, grossCents, locked, onRemove }) {
  return (
    <li className="flex items-center justify-between gap-3 text-[12px]">
      <div className="min-w-0 flex-1">
        <div className="truncate font-bold text-navy">{name}</div>
        {unitInfo && <div className="text-[10px] text-ink-3">{unitInfo}</div>}
      </div>
      <div className="text-right">
        {grossCents != null && (
          <div className="text-[10px] text-ink-3 line-through">{fmtMoney(grossCents)}</div>
        )}
        <div className="font-extrabold text-navy">{fmtMoney(cents)}</div>
      </div>
      {locked ? (
        <span
          aria-label="Locked — computed from weight"
          title="Computed from weight — edit lbs to change"
          className="flex h-8 w-8 shrink-0 items-center justify-center text-[15px] text-ink-3"
        >
          🔒
        </span>
      ) : onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove item"
          title="Remove item"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-line text-[16px] font-extrabold text-red hover:bg-red/10 active:scale-95"
        >
          ×
        </button>
      ) : (
        <span className="h-8 w-8 shrink-0" />
      )}
    </li>
  )
}

function buildWeighSummary(service, weight, preview, memberLbsUsed) {
  if (!service) return null
  if (service.pricing_mode === 'per_pound') {
    if (weight <= 0) return `— lbs · ${service.included_lbs} lbs included`
    if ((preview?.overageLbs || 0) > 0) {
      return `${weight} lbs · ${service.included_lbs} lbs included + ${preview.overageLbs.toFixed(1)} lbs extra`
    }
    return `${weight} lbs · ${service.included_lbs} lbs included`
  }
  if (service.pricing_mode === 'subscription') {
    const included = Number(service.monthly_included_lbs) || 0
    const used = Number(memberLbsUsed) || 0
    const remaining = Math.max(0, included - used)
    const overage = Math.max(0, used - included)
    const usageLine = overage > 0
      ? `Member cycle: ${used.toFixed(1)} / ${included} lbs — ${overage.toFixed(1)} lbs OVER`
      : `Member cycle: ${used.toFixed(1)} / ${included} lbs — ${remaining} lbs left`
    if (weight <= 0) return usageLine
    if (overage > 0) {
      return `${weight} lbs · ${usageLine}`
    }
    const thisOrderOver = Math.max(0, weight - remaining)
    return thisOrderOver > 0
      ? `${weight} lbs · uses last ${remaining} lbs of cycle + ${thisOrderOver.toFixed(1)} lbs overage`
      : `${weight} lbs · ${remaining - weight} lbs left after this order`
  }
  return null
}

function AddItemPicker({ catalog, onPick }) {
  const [pickedId, setPickedId] = useState('')
  const [qty, setQty] = useState(1)
  const picked = catalog.find((c) => c.id === pickedId)

  const grouped = useMemo(() => {
    const g = {}
    for (const c of catalog) {
      const cat = c.category || (c.kind === 'addon' ? 'Add-ons' : 'Items')
      if (!g[cat]) g[cat] = []
      g[cat].push(c)
    }
    return g
  }, [catalog])

  return (
    <div className="mt-3 rounded-lg border border-line bg-[#F9FAFB] p-3">
      <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-ink-2">
        Pick an item
      </label>
      <select
        value={pickedId}
        onChange={(e) => setPickedId(e.target.value)}
        className="w-full rounded-lg border border-line bg-white px-2 py-2 text-[12px]"
      >
        <option value="">— Choose —</option>
        {Object.entries(grouped).map(([cat, items]) => (
          <optgroup key={cat} label={cat}>
            {items.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name} — ${(i.price_cents / 100).toFixed(2)}
              </option>
            ))}
          </optgroup>
        ))}
      </select>

      <div className="mt-2 flex items-center gap-2">
        <div className="flex items-center gap-1 rounded-lg border border-line bg-white px-1">
          <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} disabled={qty <= 1} className="h-7 w-7 text-[13px] font-bold text-navy">−</button>
          <span className="w-6 text-center text-[12px] font-extrabold text-navy">{qty}</span>
          <button type="button" onClick={() => setQty((q) => q + 1)} className="h-7 w-7 text-[13px] font-bold text-navy">+</button>
        </div>
        <button
          type="button"
          disabled={!picked}
          onClick={() => picked && onPick(picked, qty)}
          className={`flex-1 rounded-lg bg-navy px-3 py-2 text-[12px] font-extrabold text-white ${picked ? '' : 'opacity-50'}`}
        >
          Add {picked ? `— ${picked.name}` : ''}
        </button>
      </div>
    </div>
  )
}
